import React, { useState, useEffect, useContext, useRef } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, crdEl, bdg, sp, rad, icBox } from "../../constants/styles";
import Ic from "../common/Ic";
import { TenantReviewsModal } from "../common/Reviews";

// Theme-aware status colors
const statusColor = (h) => ({
  new: { label: "New", color: h.ac },
  contacted: { label: "Contacted", color: h.wn },
  visited: { label: "Visited", color: h.pp },
  converted: { label: "Converted", color: h.gn },
  rejected: { label: "Rejected", color: h.rd }
});

const visitColor = (h) => ({
  scheduled: { label: "Scheduled", color: h.ac, icon: "\uD83D\uDCC5" },
  completed: { label: "Completed", color: h.gn, icon: "\u2705" },
  missed: { label: "Missed", color: h.rd, icon: "\u274C" }
});

// --- Deterministic lead scoring ---
function scoreLeadPriority(lead) {
  let score = 0;
  const signals = [];

  if (lead.message && lead.message.trim()) { score += 15; signals.push("Left a message"); }

  const hasPrefs = lead.pref_rooms || lead.pref_max_rent || lead.pref_furnishing;
  if (hasPrefs) { score += 10; signals.push("Shared preferences"); }

  if (lead.property_id && hasPrefs) {
    let align = 0;
    if (lead.pref_rooms && lead.prop_rooms && lead.pref_rooms === lead.prop_rooms) { align += 10; signals.push(`Wants ${lead.pref_rooms} rooms (match)`); }
    if (lead.pref_max_rent && lead.prop_rent && lead.prop_rent <= lead.pref_max_rent) { align += 10; signals.push("Budget fits"); }
    if (lead.pref_furnishing && lead.prop_furnishing && lead.pref_furnishing === lead.prop_furnishing) { align += 5; }
    score += Math.min(25, align);
  }

  if (lead.created_at) {
    const hrs = (Date.now() - new Date(lead.created_at).getTime()) / (1000 * 60 * 60);
    if (hrs < 24) { score += 25; signals.push("Today"); }
    else if (hrs < 72) { score += 15; signals.push("Recent"); }
    else if (hrs < 168) { score += 10; }
  }

  if (lead.phone && lead.phone.replace(/[\s+-]/g, '').length >= 10) { score += 5; }
  if (lead.status === "new") { score += 20; signals.push("Not yet contacted"); }

  // Visit scheduled bonus
  if (lead.visit_status === "scheduled") { score += 10; signals.push("Visit scheduled"); }

  score = Math.min(100, Math.max(0, score));
  let priority;
  if (score >= 65) priority = "hot";
  else if (score >= 40) priority = "warm";
  else priority = "cold";

  return { priority, score, signals };
}

const priColor = (h) => ({
  hot: { label: "Hot", color: h.rd, icon: "\uD83D\uDD25" },
  warm: { label: "Warm", color: h.wn, icon: "\uD83C\uDF24" },
  cold: { label: "Cold", color: h.sc, icon: "\u2744\uFE0F" }
});

export default function LeadsPage() {
  const { t, h, go, flash, plan, showConfirm } = useContext(Ctx); const hasWa = (plan.features || []).includes("whatsapp");
  const STATUS_CFG = statusColor(h);
  const PRIORITY_CFG = priColor(h);
  const VISIT_CFG = visitColor(h);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [sortBy, setSortBy] = useState("priority");
  // Which lead's reputation/reviews modal is open (vetting a prospect by phone).
  const [reviewLead, setReviewLead] = useState(null);
  // Long-press multi-select for bulk-deleting leads.
  const [selectionMode, setSelectionMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [deletingBulk, setDeletingBulk] = useState(false);
  const pressTimer = useRef(null);
  const longPressed = useRef(false);

  // Schedule modal state
  const [scheduleId, setScheduleId] = useState(null);
  const [visitDate, setVisitDate] = useState("");
  const [visitTime, setVisitTime] = useState("");
  const [scheduling, setScheduling] = useState(false);

  useEffect(() => {
    (async () => {
      const data = await api.getLeads();
      if (data.success) setLeads(data.leads);
      setLoading(false);
    })();
  }, []);

  const updateStatus = async (id, status) => {
    const res = await api.updateLead(id, status);
    if (res.success) {
      setLeads(prev => prev.map(l => l.id === id ? { ...l, status } : l));
      flash(t.updated || "Updated");
    }
  };

  const scheduleVisit = async () => {
    if (!visitDate || !visitTime) return;
    setScheduling(true);
    const res = await api.scheduleLead(scheduleId, visitDate, visitTime);
    if (res.success) {
      setLeads(prev => prev.map(l => l.id === scheduleId ? {
        ...l, visit_date: visitDate, visit_time: visitTime, visit_status: "scheduled",
        status: l.status === "new" ? "contacted" : l.status
      } : l));
      flash(t.visitScheduled || "Visit Scheduled!");
    }
    setScheduleId(null);
    setVisitDate("");
    setVisitTime("");
    setScheduling(false);
  };

  const updateVisit = async (id, visit_status) => {
    const res = await api.updateVisitStatus(id, visit_status);
    if (res.success) {
      setLeads(prev => prev.map(l => l.id === id ? {
        ...l, visit_status,
        status: visit_status === "completed" ? "visited" : l.status
      } : l));
      flash(t.updated || "Updated");
    }
  };

  const openSchedule = (lead) => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setVisitDate(lead.visit_date || tomorrow.toISOString().split('T')[0]);
    setVisitTime(lead.visit_time || "10:00");
    setScheduleId(lead.id);
  };

  // Hard-delete a lead. Confirms first (destructive — no undo), then
  // fires the API, then drops the row from local state on success.
  // On failure, leave the local state intact so the row reappears as
  // soon as the next render kicks in — the user can retry.
  const handleDelete = (lead) => {
    showConfirm(
      `Delete the lead "${lead.name}"? This cannot be undone.`,
      async () => {
        const res = await api.deleteLead(lead.id);
        if (res.success) {
          setLeads(prev => prev.filter(l => l.id !== lead.id));
          if (expandedId === lead.id) setExpandedId(null);
          flash(t.leadDeleted || "Lead deleted");
        } else {
          flash(res.message || (t.deleteFailed || "Delete failed"), "error");
        }
      }
    );
  };

  // Long-press (touch hold / mouse hold) enters multi-select mode. A move or
  // early release cancels it; the follow-up click is swallowed so the pressed
  // card doesn't immediately toggle back off or expand.
  const startPress = (id) => {
    longPressed.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      longPressed.current = true;
      setExpandedId(null);
      setSelectionMode(true);
      setSelected(prev => { const n = new Set(prev); n.add(id); return n; });
    }, 500);
  };
  const cancelPress = () => { clearTimeout(pressTimer.current); };
  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const exitSelection = () => { setSelectionMode(false); setSelected(new Set()); };
  const onLeadCardClick = (lead) => {
    if (longPressed.current) { longPressed.current = false; return; } // swallow the click right after a long-press
    if (selectionMode) { toggleSelect(lead.id); return; }
    setExpandedId(expandedId === lead.id ? null : lead.id);
  };

  // Bulk hard-delete the selected leads (same effect as the single Delete Lead).
  const bulkDelete = () => {
    if (selected.size === 0 || deletingBulk) return;
    const ids = [...selected];
    showConfirm(
      ids.length === 1 ? `Delete this lead? This cannot be undone.` : `Delete ${ids.length} leads? This cannot be undone.`,
      async () => {
        setDeletingBulk(true);
        const results = await Promise.all(ids.map(id =>
          api.deleteLead(id).then(r => ({ id, ok: !!(r && r.success) })).catch(() => ({ id, ok: false }))
        ));
        setDeletingBulk(false);
        const okIds = new Set(results.filter(r => r.ok).map(r => r.id));
        if (okIds.size > 0) {
          setLeads(prev => prev.filter(l => !okIds.has(l.id)));
          if (okIds.has(expandedId)) setExpandedId(null);
        }
        exitSelection();
        const failed = ids.length - okIds.size;
        if (failed > 0) flash(`${failed} ${failed === 1 ? "lead" : "leads"} couldn't be deleted`, "error");
        else flash(`${okIds.size} ${okIds.size === 1 ? "lead" : "leads"} ${t.deleted || "deleted"}`);
      }
    );
  };

  const filters = ["all", "new", "contacted", "visited", "converted", "rejected"];
  const filtered = filter === "all" ? leads : leads.filter(l => l.status === filter);

  const scored = filtered.map(l => ({ ...l, _score: scoreLeadPriority(l) }));
  if (sortBy === "priority") {
    scored.sort((a, b) => b._score.score - a._score.score);
  }

  const newCount = leads.filter(l => l.status === "new").length;
  const hotCount = leads.filter(l => scoreLeadPriority(l).priority === "hot").length;

  // Visit stats
  const today = new Date().toISOString().split('T')[0];
  const todayVisits = leads.filter(l => l.visit_date === today && l.visit_status === "scheduled");
  const upcomingVisits = leads.filter(l => l.visit_date > today && l.visit_status === "scheduled");

  return <div style={{ paddingTop: sp.lg }}>
    {selectionMode ? (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg, background: h.cd, padding: "8px 8px 8px 14px", borderRadius: rad.md, border: `1px solid ${h.bd}` }}>
        <span style={{ color: h.tx, fontSize: 14, fontWeight: 700 }}>{selected.size} {t.selected || "selected"}</span>
        <div style={{ display: "flex", gap: sp.sm }}>
          <button onClick={exitSelection} style={{ padding: "7px 14px", borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.cancel || "Cancel"}</button>
          <button onClick={bulkDelete} disabled={selected.size === 0 || deletingBulk} style={{ padding: "7px 14px", borderRadius: rad.sm, border: "none", background: h.rd, color: "#fff", cursor: selected.size === 0 || deletingBulk ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, opacity: selected.size === 0 || deletingBulk ? 0.5 : 1, display: "flex", alignItems: "center", gap: 5 }}>{"\uD83D\uDDD1\uFE0F "}{deletingBulk ? (t.deleting || "Deleting\u2026") : (t.delete || "Delete")}</button>
        </div>
      </div>
    ) : (
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg }}>
        <h2 style={{ color: h.tx, fontSize: 18, fontWeight: 700, margin: 0, letterSpacing: "-0.2px" }}>{t.leads || "Leads"}</h2>
        <div style={{ display: "flex", gap: sp.xs + 2, alignItems: "center" }}>
          {hotCount > 0 && <span style={bdg(`${h.rd}18`, h.rd)}>{"\uD83D\uDD25"} {hotCount} {t.hotLeads || "Hot"}</span>}
          {newCount > 0 && <span style={{ ...bdg(h.ac, "#fff"), fontWeight: 700 }}>{newCount} {t.leadNew || "New"}</span>}
        </div>
      </div>
    )}

    {/* Today's visits banner */}
    {todayVisits.length > 0 && <div style={{ background: `${h.ac}10`, border: `1px solid ${h.ac}30`, borderRadius: rad.md, padding: `${sp.md}px ${sp.lg - 2}px`, marginBottom: sp.md }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm, marginBottom: sp.xs + 2 }}>
        <div style={icBox(h.ac, 28)}>{"\uD83D\uDCC5"}</div>
        <span style={{ color: h.tx, fontSize: 13, fontWeight: 700 }}>{t.todaysVisits || "Today's Visits"} ({todayVisits.length})</span>
      </div>
      {todayVisits.map(v => <div key={v.id} onClick={() => setExpandedId(v.id)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: `${sp.xs + 2}px 0`, cursor: "pointer" }}>
        <div>
          <span style={{ color: h.tx, fontSize: 12, fontWeight: 600 }}>{v.name}</span>
          <span style={{ color: h.ts, fontSize: 11, marginLeft: sp.xs + 2 }}>{v.visit_time}</span>
        </div>
        {v.property_addr && <span style={{ color: h.ts, fontSize: 10 }}>{v.property_addr}</span>}
      </div>)}
    </div>}

    {/* Upcoming visits banner */}
    {upcomingVisits.length > 0 && <div style={{ background: `${h.gn}08`, border: `1px solid ${h.gn}25`, borderRadius: rad.md, padding: `${sp.md}px ${sp.lg - 2}px`, marginBottom: sp.md }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
        <div style={icBox(h.gn, 28)}>{"\uD83D\uDDD3"}</div>
        <span style={{ color: h.tx, fontSize: 12, fontWeight: 600 }}>{t.upcomingVisits || "Upcoming Visits"}: {upcomingVisits.length}</span>
      </div>
    </div>}

    {/* Sort toggle */}
    <div style={{ display: "flex", gap: sp.xs + 2, marginBottom: sp.md }}>
      {[["priority", t.sortPriority || "By Priority"], ["date", t.sortDate || "By Date"]].map(([key, label]) => <button key={key} onClick={() => setSortBy(key)} style={{
        padding: `${sp.xs}px ${sp.sm + 2}px`, borderRadius: rad.sm, cursor: "pointer", fontSize: 11, fontWeight: sortBy === key ? 600 : 400,
        border: `1px solid ${sortBy === key ? h.ac : h.bd}`,
        background: sortBy === key ? `${h.ac}15` : "transparent", color: sortBy === key ? h.ac : h.ts,
        transition: "all 0.2s ease"
      }}>{label}</button>)}
    </div>

    {/* Filter tabs */}
    <div className="hide-scrollbar" style={{ display: "flex", gap: sp.xs + 2, marginBottom: sp.lg, overflowX: "auto", paddingBottom: sp.xs }}>
      {filters.map(f => {
        const count = f === "all" ? leads.length : leads.filter(l => l.status === f).length;
        const cfg = STATUS_CFG[f];
        const isActive = filter === f;
        return <button key={f} onClick={() => setFilter(f)} style={{
          padding: `${sp.xs + 1}px ${sp.md}px`, borderRadius: rad.pill, cursor: "pointer", fontSize: 11, fontWeight: isActive ? 600 : 400,
          border: `1px solid ${isActive ? (cfg?.color || h.ac) : h.bd}`,
          background: isActive ? `${cfg?.color || h.ac}15` : "transparent",
          color: isActive ? (cfg?.color || h.ac) : h.ts, whiteSpace: "nowrap",
          transition: "all 0.2s ease"
        }}>{f === "all" ? (t.allProps || "All") : (t["lead" + f.charAt(0).toUpperCase() + f.slice(1)] || cfg?.label || f)} ({count})</button>;
      })}
    </div>

    <div data-coach="leads-list">
    {loading ? <div style={{ textAlign: "center", padding: 40 }}>
      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div> : scored.length === 0 ? <div className="animate-fadeIn" style={{ ...crd(h), textAlign: "center", padding: sp.xxl }}>
      <div style={{ fontSize: 32, marginBottom: sp.sm }}>{"\uD83D\uDCED"}</div>
      <div style={{ color: h.ts, fontSize: 13 }}>{t.noLeads || "No leads yet"}</div>
    </div> : scored.map((lead, idx) => {
      const cfg = STATUS_CFG[lead.status] || STATUS_CFG.new;
      const date = lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "";
      const isExpanded = expandedId === lead.id;
      const pri = lead._score;
      const priCfg = PRIORITY_CFG[pri.priority];
      const vCfg = lead.visit_status ? VISIT_CFG[lead.visit_status] : null;

      return <div key={lead.id} className="animate-fadeIn" style={{ ...crd(h), animationDelay: `${Math.min(idx * 40, 200)}ms`, animationFillMode: "backwards", userSelect: "none", WebkitUserSelect: "none", ...(selected.has(lead.id) ? { border: `2px solid ${h.ac}`, background: `${h.ac}0d` } : {}) }}>
        <div onClick={() => onLeadCardClick(lead)}
          onTouchStart={() => startPress(lead.id)} onTouchEnd={cancelPress} onTouchMove={cancelPress}
          onMouseDown={() => startPress(lead.id)} onMouseUp={cancelPress} onMouseLeave={cancelPress}
          onContextMenu={(e) => e.preventDefault()}
          style={{ cursor: "pointer" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            {selectionMode && <span style={{ width: 20, height: 20, borderRadius: 999, border: `2px solid ${selected.has(lead.id) ? h.ac : h.bd}`, background: selected.has(lead.id) ? h.ac : "transparent", flexShrink: 0, marginRight: 8, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>{selected.has(lead.id) && <span style={{ color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1 }}>{"✓"}</span>}</span>}
            <div style={{ flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: sp.xs + 2 }}>
                <div style={icBox(priCfg.color, 28)}><span style={{ fontSize: 13 }}>{priCfg.icon}</span></div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ color: h.tx, fontWeight: 600, fontSize: 14, letterSpacing: "-0.1px" }}>{lead.name}</span>
                    {/* Reputation link — vet this prospect's reviews before contacting.
                        stopPropagation so tapping it doesn't toggle the card open/closed. */}
                    <span onClick={(e) => { e.stopPropagation(); setReviewLead(lead); }} title={t.viewTenantReviews || "View tenant reviews"}
                      style={{ background: `${h.ac}12`, color: h.ac, padding: "1px 8px", borderRadius: 999, fontSize: 9, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 3 }}>
                      <span style={{ color: "#f59e0b" }}>{"★"}</span>{t.reviews || "Reviews"}<span style={{ opacity: 0.7 }}>{"›"}</span>
                    </span>
                  </div>
                  <div style={{ color: h.ts, fontSize: 11, marginTop: 1 }}>{lead.phone}</div>
                </div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: sp.xs }}>
              <span style={bdg(`${cfg.color}18`, cfg.color)}>{t["lead" + lead.status.charAt(0).toUpperCase() + lead.status.slice(1)] || cfg.label}</span>
              <span style={{ ...bdg(`${priCfg.color}12`, priCfg.color), fontSize: 9, padding: "2px 8px" }}>
                {priCfg.label} {"\u00B7"} {pri.score}%
              </span>
            </div>
          </div>
          {lead.property_addr && <div style={{ color: h.ac, fontSize: 11, marginTop: sp.xs + 2, display: "flex", alignItems: "center", gap: sp.xs }}>
            <Ic n="bldg" s={12} c={h.ac} />{lead.property_addr}
          </div>}
          {/* Visit info badge */}
          {vCfg && lead.visit_date && <div style={{ display: "flex", alignItems: "center", gap: sp.xs + 2, marginTop: sp.xs + 2, background: `${vCfg.color}10`, padding: `${sp.xs}px ${sp.sm + 2}px`, borderRadius: rad.sm }}>
            <span style={{ fontSize: 12 }}>{vCfg.icon}</span>
            <span style={{ color: vCfg.color, fontSize: 11, fontWeight: 600 }}>
              {t["visit" + lead.visit_status.charAt(0).toUpperCase() + lead.visit_status.slice(1)] || vCfg.label}
            </span>
            <span style={{ color: h.ts, fontSize: 10 }}>
              {new Date(lead.visit_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} {"\u00B7"} {lead.visit_time}
            </span>
          </div>}
          {/* Preference summary */}
          {(lead.pref_rooms || lead.pref_max_rent || lead.pref_furnishing) && <div style={{ display: "flex", gap: sp.xs, marginTop: sp.xs, flexWrap: "wrap" }}>
            {lead.pref_rooms && <span style={{ ...bdg(`${h.ac}10`, h.ac), fontSize: 9, padding: "1px 6px" }}>{lead.pref_rooms} {t.rooms || "rooms"}</span>}
            {lead.pref_max_rent && <span style={{ ...bdg(`${h.ac}10`, h.ac), fontSize: 9, padding: "1px 6px" }}>{"\u2264\u20B9"}{parseInt(lead.pref_max_rent).toLocaleString()}</span>}
            {lead.pref_furnishing && <span style={{ ...bdg(`${h.ac}10`, h.ac), fontSize: 9, padding: "1px 6px" }}>{lead.pref_furnishing}</span>}
            {lead.pref_move_in && <span style={{ ...bdg(`${h.ac}10`, h.ac), fontSize: 9, padding: "1px 6px" }}>{lead.pref_move_in}</span>}
          </div>}
          {lead.message && <div style={{ color: h.ts, fontSize: 11, marginTop: sp.xs, fontStyle: "italic", lineHeight: 1.4 }}>"{lead.message}"</div>}
          {/* Follow-up sent badge */}
          {lead.follow_up_sent === 1 && <div style={{ display: "inline-flex", alignItems: "center", gap: sp.xs, marginTop: sp.xs, background: `${h.wn}12`, padding: "2px 8px", borderRadius: rad.sm }}>
            <span style={{ fontSize: 10 }}>{"\uD83D\uDD14"}</span>
            <span style={{ color: h.wn, fontSize: 9, fontWeight: 600 }}>{t.followUpSent || "Follow-up sent"}</span>
            {lead.last_follow_up_at && <span style={{ color: h.ts, fontSize: 8 }}>{"\u00B7"} {new Date(lead.last_follow_up_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}</span>}
          </div>}
          {pri.signals.length > 0 && <div style={{ display: "flex", gap: sp.xs, marginTop: sp.xs, flexWrap: "wrap" }}>
            {pri.signals.slice(0, 3).map((s, i) => <span key={i} style={{ color: h.tt, fontSize: 9 }}>{"\u00B7"} {s}</span>)}
          </div>}
          <div style={{ color: h.tt, fontSize: 10, marginTop: sp.xs + 2 }}>{date}</div>
        </div>

        {reviewLead && reviewLead.id === lead.id && (
          <TenantReviewsModal phone={lead.phone} name={lead.name} h={h} t={t} flash={flash} onClose={() => setReviewLead(null)} />
        )}

        {isExpanded && <div style={{ borderTop: `1px solid ${h.bd}`, marginTop: sp.sm + 2, paddingTop: sp.sm + 2 }}>
          {/* Quick contact actions */}
          <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm + 2 }}>
            <a href={`tel:${lead.phone}`} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs + 2, padding: `${sp.sm + 2}px ${sp.md}px`, borderRadius: rad.sm + 2, background: `${h.gn}12`, border: `1px solid ${h.gn}25`, color: h.gn, textDecoration: "none", fontSize: 12, fontWeight: 600, transition: "all 0.2s ease" }}>
              {"\uD83D\uDCDE"} {t.callLead || "Call"}
            </a>
            {hasWa && <a href={`https://wa.me/${lead.phone.replace(/[\s+-]/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs + 2, padding: `${sp.sm + 2}px ${sp.md}px`, borderRadius: rad.sm + 2, background: "#25D36612", border: "1px solid #25D36625", color: "#25D366", textDecoration: "none", fontSize: 12, fontWeight: 600, transition: "all 0.2s ease" }}>
              {"\uD83D\uDCAC"} {t.waLead || "WhatsApp"}
            </a>}
          </div>

          {/* Schedule visit button */}
          {lead.status !== "converted" && lead.status !== "rejected" && <button onClick={() => openSchedule(lead)} style={{
            width: "100%", padding: `${sp.sm + 2}px ${sp.lg - 2}px`, borderRadius: rad.sm + 2, border: `1px solid ${h.ac}30`,
            background: `${h.ac}08`, color: h.ac, cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: sp.sm + 2,
            display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs + 2, transition: "all 0.2s ease"
          }}>
            {"\uD83D\uDCC5"} {lead.visit_status === "scheduled" ? (t.rescheduleVisit || "Reschedule Visit") : (t.scheduleVisit || "Schedule Visit")}
          </button>}

          {/* Visit action buttons */}
          {lead.visit_status === "scheduled" && <div style={{ display: "flex", gap: sp.sm, marginBottom: sp.sm + 2 }}>
            <button onClick={() => updateVisit(lead.id, "completed")} style={{
              flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.gn}30`,
              background: `${h.gn}08`, color: h.gn, cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all 0.2s ease"
            }}>{"\u2705"} {t.visitCompleted || "Completed"}</button>
            <button onClick={() => updateVisit(lead.id, "missed")} style={{
              flex: 1, padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.rd}30`,
              background: `${h.rd}08`, color: h.rd, cursor: "pointer", fontSize: 11, fontWeight: 600, transition: "all 0.2s ease"
            }}>{"\u274C"} {t.visitMissed || "Missed"}</button>
          </div>}

          {/* Convert to Tenant (only for visited/contacted leads) */}
          {(lead.status === "visited" || lead.status === "contacted") && <button onClick={async () => {
            const res = await api.convertLead(lead.id);
            if (res.success) {
              setLeads(leads.map(l => l.id === lead.id ? { ...l, status: "converted" } : l));
              flash(res.message || `✅ Association request sent to ${lead.name}!`);
            } else flash(res.message || "Conversion failed", "error");
          }} style={{
            width: "100%", padding: `${sp.sm + 2}px ${sp.lg - 2}px`, borderRadius: rad.sm + 2, border: `1px solid ${h.gn}30`,
            background: `${h.gn}08`, color: h.gn, cursor: "pointer", fontSize: 12, fontWeight: 700, marginBottom: sp.sm + 2,
            display: "flex", alignItems: "center", justifyContent: "center", gap: sp.xs + 2
          }}>
            {"🔄"} Convert to Tenant
          </button>}

          {/* Status actions */}
          <div style={{ display: "flex", gap: sp.xs + 2, flexWrap: "wrap" }}>
            {["new", "contacted", "visited", "converted", "rejected"].filter(s => s !== lead.status).map(s => {
              const sc = STATUS_CFG[s];
              return <button key={s} onClick={() => updateStatus(lead.id, s)} style={{
                padding: `${sp.xs + 2}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${sc.color}30`,
                background: `${sc.color}08`, color: sc.color, cursor: "pointer", fontSize: 10, fontWeight: 600,
                transition: "all 0.2s ease"
              }}>{t["lead" + s.charAt(0).toUpperCase() + s.slice(1)] || sc.label}</button>;
            })}
          </div>

          {/* Destructive action — separated from the status row by a thin
              divider so it doesn't read as just another status. Mirrors the
              "Delete Agreement" pattern at the bottom of AgreementPage's
              detail view. Confirmation is mandatory (handleDelete uses
              showConfirm) since this hits both client state and the DB. */}
          <div style={{
            marginTop: sp.md, paddingTop: sp.sm + 2,
            borderTop: `1px dashed ${h.bd}50`,
          }}>
            <button onClick={() => handleDelete(lead)} style={{
              width: "100%",
              padding: `${sp.sm + 2}px ${sp.lg - 2}px`,
              borderRadius: rad.sm + 2,
              border: `1px solid ${h.rd}30`,
              background: `${h.rd}08`,
              color: h.rd,
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              display: "flex", alignItems: "center", justifyContent: "center",
              gap: sp.xs + 2,
              transition: "all 0.2s ease",
            }}>
              {"🗑️"} {t.deleteLead || "Delete Lead"}
            </button>
          </div>
        </div>}
      </div>;
    })}
    </div>

    {/* Schedule Visit Modal */}
    {scheduleId && <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: h.ov, zIndex: 999, display: "flex", alignItems: "center", justifyContent: "center", padding: sp.xl }} className="animate-fadeIn">
      <div className="animate-scaleIn" style={{ ...crdEl(h), padding: sp.xxl, width: "100%", maxWidth: 340, boxShadow: h.s3 }}>
        <h3 style={{ color: h.tx, fontSize: 16, fontWeight: 700, margin: `0 0 ${sp.lg}px 0` }}>{"\uD83D\uDCC5"} {t.scheduleVisit || "Schedule Visit"}</h3>

        <div style={{ marginBottom: sp.lg - 2 }}>
          <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: sp.xs, letterSpacing: "0.3px", textTransform: "uppercase" }}>{t.visitDate || "Date"}</label>
          <input type="date" value={visitDate} onChange={e => setVisitDate(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            style={{ width: "100%", padding: `${sp.sm + 2}px ${sp.md}px`, borderRadius: rad.sm + 2, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 14, boxSizing: "border-box", transition: "border-color 0.2s, box-shadow 0.2s" }} />
        </div>

        <div style={{ marginBottom: sp.xl }}>
          <label style={{ color: h.ts, fontSize: 11, fontWeight: 600, display: "block", marginBottom: sp.xs, letterSpacing: "0.3px", textTransform: "uppercase" }}>{t.visitTime || "Time"}</label>
          <input type="time" value={visitTime} onChange={e => setVisitTime(e.target.value)}
            style={{ width: "100%", padding: `${sp.sm + 2}px ${sp.md}px`, borderRadius: rad.sm + 2, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 14, boxSizing: "border-box", transition: "border-color 0.2s, box-shadow 0.2s" }} />
        </div>

        <div style={{ display: "flex", gap: sp.sm + 2 }}>
          <button onClick={() => { setScheduleId(null); setVisitDate(""); setVisitTime(""); }}
            style={{ flex: 1, padding: `${sp.sm + 2}px`, borderRadius: rad.sm + 2, border: `1px solid ${h.bd}`, background: "transparent", color: h.ts, cursor: "pointer", fontSize: 13, fontWeight: 600, transition: "all 0.2s ease" }}>
            {t.cancel || "Cancel"}
          </button>
          <button onClick={scheduleVisit} disabled={scheduling || !visitDate || !visitTime}
            style={{ flex: 1, padding: `${sp.sm + 2}px`, borderRadius: rad.sm + 2, border: "none", background: h.ac, color: "#fff", cursor: scheduling ? "default" : "pointer", fontSize: 13, fontWeight: 600, opacity: scheduling ? 0.6 : 1, boxShadow: `0 2px 8px ${h.ac}30`, transition: "all 0.2s ease" }}>
            {scheduling ? "..." : (t.confirm || "Confirm")}
          </button>
        </div>
      </div>
    </div>}
  </div>;
}
